import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ClipboardList, Plus, ChevronRight, ArrowLeft, TrendingUp, TrendingDown,
  CheckCircle2, ExternalLink, AlertTriangle, FileText, Upload, Download,
  Trash2, MapPin, Clock, FileCheck2, Paperclip,
  Briefcase, DraftingCompass, CalendarRange, Shield, FolderOpen, Sparkles,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Pill, Button, Field, Input, Select, Spinner, EmptyState,
} from "@/components/ui";
import { fmtMoney, fmtDate, daysFromNow } from "@/lib/dates";
import { distanceFromBneGpoKm } from "@/lib/geo";
import { DistanceFromBne } from "@/components/DistanceFromBne";
import BidNotepadCard from "./BidNotepadCard";
import JobFolderCard from "@/components/JobFolderCard";
import BidDeliverablesCard from "./BidDeliverablesCard";
import DesignPanel from "@/features/projects/DesignPanel";
import BidSchedulePlanCard from "./BidSchedulePlanCard";
import ProgramPanel from "@/features/projects/ProgramPanel";
import BidSitesCard from "./BidSitesCard";
import SiteMapsCard from "@/features/sitemaps/SiteMapsCard";

// =============================================================================
// Bids / RFQs — list + detail
// =============================================================================
// Detail screen layout (22 Apr 2026 revamp):
//   ← Bids
//   [BID-26-0001] [STATUS]   ... title ...   [Won—promote] [Lost]
//   Client · Carrier · ref
//
//   ┌─ Summary ───────────┐ ┌─ Linked quotes ─┐
//   │ Value / Due / Site  │ │ Q-26-0001 · DRAFT│
//   │ Distance from BNE   │ │ [+ New quote]    │
//   └─────────────────────┘ └──────────────────┘
//
//   ┌─ Scope ────────────────────────────────────┐
//
//   ┌─ Documents ────────────────────────────────┐
//   │ [drop files here]                          │
//   │ 3 files — Design / Scope / RFQ             │
// =============================================================================

// -----------------------------------------------------------------------------
// List screen
// -----------------------------------------------------------------------------
export function BidsListScreen() {
  const user = useRequireAuth();
  const qc = useQueryClient();
  // T-383 — default view = ACTIVE pipeline (open + responded). Won bids have
  // become projects and lost/no-bid/withdrawn are history — both reachable via
  // their tabs or All, but they no longer clog the working list.
  const [status, setStatus] = useState<"active" | "won" | "lost" | "all">("active");
  const [clientFilter, setClientFilter] = useState("");   // T-358 — client_code or ""
  const [carrierFilter, setCarrierFilter] = useState("");  // T-358 — carrier_code or ""
  const isDirector = hasRole(user, "director");
  const canSeeBids = hasRole(user, "manager");

  // Field / supervisor / subcontractor don't see commercial pipeline.
  // The nav tile is already gated (ProjectHubScreen), but lock the route
  // too in case someone types /bids directly. James 2026-04-30.
  if (user && !canSeeBids) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <EmptyState
            title="No access"
            description="The Bids / RFQs pipeline is restricted to manager-and-above roles. Speak to a director if you think this is a mistake."
            icon={ClipboardList}
          />
        </Card>
      </div>
    );
  }

  const { data: bids = [], isLoading } = useQuery({
    queryKey: ["bids_list", status],
    queryFn: async () => {
      let q = supabase
        .from("bids")
        .select(`
          bid_id, bid_number, bid_title, client_ref, status,
          due_date, estimated_value, created_at,
          clients(legal_name, client_code),
          carriers(carrier_code),
          sites(site_code)
        `)
        .order("due_date", { ascending: true, nullsFirst: false })
        .limit(200);
      if (status === "active")    q = q.in("status", ["open", "responded"]);
      else if (status === "lost") q = q.in("status", ["lost", "no_bid", "withdrawn"]);
      else if (status !== "all")  q = q.eq("status", status);
      const { data, error } = await q;
      if (error) throw error;
      return data as any[];
    },
  });

  // Director-only BID delete. Two-step safety:
  //   1. Refuse if any project has this bid as source_bid_id (real promotion).
  //   2. Otherwise unlink draft quotes (set bid_id = null), delete the bid.
  // The quotes survive — they just lose the bid linkage. Easier than cascade
  // because we never want to surprise-delete a quote that has line items.
  const deleteBidMut = useMutation({
    mutationFn: async (bid: { bid_id: string; bid_number: string }) => {
      // Step 1: any linked projects?
      const { data: projects, error: pErr } = await supabase
        .from("projects")
        .select("project_id, project_code")
        .eq("source_bid_id", bid.bid_id);
      if (pErr) throw new Error(`Could not check linked projects: ${pErr.message}`);
      if (projects && projects.length > 0) {
        const codes = projects.map(p => p.project_code).join(", ");
        throw new Error(`Cannot delete ${bid.bid_number} — promoted to project ${codes}. Detach or delete the project first if you really want to nuke this bid.`);
      }
      // Step 2: unlink any quotes
      const { error: qErr } = await supabase
        .from("quotes")
        .update({ bid_id: null })
        .eq("bid_id", bid.bid_id);
      if (qErr) throw new Error(`Could not unlink quotes: ${qErr.message}`);
      // Step 3: delete the bid
      const { error: bErr } = await supabase
        .from("bids")
        .delete()
        .eq("bid_id", bid.bid_id);
      if (bErr) {
        const parts = [bErr.message, (bErr as any).details, (bErr as any).hint].filter(Boolean);
        throw new Error(parts.join(" — "));
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bids_list"] }),
    onError: (e: any) => window.alert(e?.message ?? "Delete failed"),
  });

  function handleDelete(e: React.MouseEvent, bid: { bid_id: string; bid_number: string; bid_title: string }) {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete ${bid.bid_number} — ${bid.bid_title}?\n\nThis cannot be undone. Linked draft quotes will be unlinked but kept.`)) return;
    deleteBidMut.mutate(bid);
  }

  // T-358 — client + carrier filter options, derived from the loaded bids.
  const clientOptions = Array.from(
    new Map((bids as any[]).filter(b => b.clients?.client_code).map(b => [b.clients.client_code, b.clients.legal_name])).entries()
  );
  const carrierOptions = Array.from(new Set((bids as any[]).filter(b => b.carriers?.carrier_code).map(b => b.carriers.carrier_code as string)));
  const filteredBids = (bids as any[]).filter(b =>
    (!clientFilter  || b.clients?.client_code === clientFilter) &&
    (!carrierFilter || b.carriers?.carrier_code === carrierFilter)
  );

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto">
      <Link
        to="/project"
        className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3"
      >
        <ArrowLeft size={14}/> Project portal
      </Link>

      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Bids / RFQs</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Tender opportunities in flight. Promote to full project on win.
          </div>
        </div>
        {hasRole(user, "supervisor") && (
          <Link to="/projects/new"><Button icon={Plus}>New BID</Button></Link>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4">
        {([
          ["active", "Active"],
          ["won",    "Won"],
          ["lost",   "Lost / No bid"],
          ["all",    "All"],
        ] as const).map(([v, label]) => (
          <button
            key={v}
            onClick={() => setStatus(v)}
            className={`px-3 py-1.5 rounded text-[11px] font-mono uppercase tracking-wider2 font-semibold border transition-colors ${
              status === v ? "bg-accent/12 text-accent border-accent/30" : "bg-ink-raised text-text-muted border-line"
            }`}
          >
            {label}
          </button>
        ))}

        {/* T-358 — client + carrier filters */}
        <select value={clientFilter} onChange={e => setClientFilter(e.target.value)}
          className="ml-1 px-2 py-1.5 rounded text-[12px] bg-ink-raised text-text border border-line outline-none">
          <option value="">All clients</option>
          {clientOptions.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
        </select>
        <select value={carrierFilter} onChange={e => setCarrierFilter(e.target.value)}
          className="px-2 py-1.5 rounded text-[12px] bg-ink-raised text-text border border-line outline-none">
          <option value="">All carriers</option>
          {carrierOptions.map(code => <option key={code} value={code}>{code}</option>)}
        </select>
        {(clientFilter || carrierFilter) && (
          <button onClick={() => { setClientFilter(""); setCarrierFilter(""); }}
            className="text-[11px] text-accent hover:underline">Clear</button>
        )}
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : filteredBids.length === 0 ? (
        <Card><EmptyState title="No bids" description={bids.length > 0 ? "No bids match these filters." : "New BIDs show up here while in progress."} icon={ClipboardList} /></Card>
      ) : (
        <div className="space-y-2">
          {filteredBids.map((b) => {
            const days = daysFromNow(b.due_date);
            const urgent = b.status === "open" && days != null && days <= 3;
            return (
              <Link key={b.bid_id} to={`/bids/${b.bid_id}`}>
                <Card>
                  <div className="p-4 flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${
                      b.status === "won" ? "bg-ok/12 text-ok" :
                      b.status === "lost" ? "bg-text-muted/15 text-text-muted" :
                      urgent ? "bg-danger/12 text-danger" :
                      "bg-accent/12 text-accent"
                    }`}>
                      <ClipboardList size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[11px] text-accent">{b.bid_number}</span>
                        <Pill label={b.status} tone={b.status === "won" ? "won" : b.status === "lost" ? "lost" : "bid"} small />
                        {urgent && <Pill label={`due in ${days}d`} tone="critical" small />}
                      </div>
                      <div className="text-[14px] text-text mt-0.5 truncate">{b.bid_title}</div>
                      <div className="text-[11px] font-mono text-text-muted mt-0.5">
                        {b.clients?.legal_name ?? "—"}
                        {b.carriers?.carrier_code && ` · ${b.carriers.carrier_code}`}
                        {b.due_date && ` · due ${fmtDate(b.due_date)}`}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono text-[13px] text-text">{fmtMoney(b.estimated_value)}</div>
                    </div>
                    {isDirector && (
                      <button
                        type="button"
                        onClick={(e) => handleDelete(e, b)}
                        title={`Delete ${b.bid_number}`}
                        className="p-1.5 rounded text-text-dim hover:text-danger hover:bg-danger/10 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                    <ChevronRight size={14} className="text-text-dim mt-2" />
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Detail screen
// -----------------------------------------------------------------------------
type BidTab = "overview" | "design" | "plan" | "documents" | "hse";
const BID_TABS: BidTab[] = ["overview", "design", "plan", "documents", "hse"];

export function BidDetailScreen() {
  const { bidId } = useParams<{ bidId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const [showPromote, setShowPromote] = useState(false);
  // 27 Oct 2026 — bid page tidy. Eleven cards in one column became five tabs,
  // the same bar as the project page, with a status strip up top so a BID
  // reads at a glance: value, due, quotes, documents, design, program.
  const [sp] = useSearchParams();
  const urlTab = sp.get("tab") as BidTab | null;
  const [tab, setTab] = useState<BidTab>(urlTab && BID_TABS.includes(urlTab) ? urlTab : "overview");
  useEffect(() => { if (urlTab && BID_TABS.includes(urlTab)) setTab(urlTab); }, [urlTab]);

  const { data: bid, isLoading, refetch } = useQuery({
    queryKey: ["bid", bidId],
    enabled: !!bidId,
    queryFn: async () => {
      // `sites(*)` so we pick up latitude/longitude/address regardless of
      // current schema shape — defensive read, matches the pattern we use
      // on Quote PDF generation.
      const { data, error } = await supabase
        .from("bids")
        .select(`
          bid_id, bid_number, bid_title, client_ref, status,
          due_date, estimated_value, scope_summary,
          created_at, promoted_to_project_id, sharepoint_folder_url,
          clients(client_id, client_code, legal_name),
          carriers(carrier_id, carrier_code),
          sites(*, asset_owner:asset_owners(owner_code, display_name))
        `)
        .eq("bid_id", bidId!)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });

  // the strip's counts — light reads, one each
  const { data: strip } = useQuery({
    queryKey: ["bid_strip", bidId],
    enabled: !!bidId,
    queryFn: async () => {
      const [q, d, des, prog] = await Promise.all([
        supabase.from("quotes").select("quote_id, status, subtotal_excl_gst, pdf_storage_path").eq("bid_id", bidId!),
        (supabase as any).from("bid_documents").select("id, doc_type, file_name").eq("bid_id", bidId!),
        (supabase as any).from("project_designs").select("design_id, source, updated_at").eq("bid_id", bidId!).maybeSingle(),
        (supabase as any).from("project_program_activities").select("activity_id").eq("bid_id", bidId!).eq("is_active", true).limit(1),
      ]);
      const quotes = (q.data ?? []) as any[]; const docs = (d.data ?? []) as any[];
      const live = quotes.filter(x => !["draft", "superseded", "withdrawn", "expired", "rejected"].includes(x.status));
      const designish = docs.filter(x => ["design", "drawing", "scope", "specification", "sow"].includes(x.doc_type) || /ffc|aaafc|l3fc|\bfc\b|\bdd\b|design brief|sdb|drawing/i.test(x.file_name ?? ""));
      return {
        quotes: quotes.length, sent: quotes.filter(x => x.status === "sent").length, accepted: quotes.filter(x => ["accepted", "partially_invoiced", "invoiced"].includes(x.status)).length,
        quoted: live.reduce((a, x) => a + Number(x.subtotal_excl_gst ?? 0), 0), drafts: quotes.filter(x => x.status === "draft").length,
        docs: docs.length, designDocs: designish.length, design: des.data ?? null, program: ((prog.data ?? []) as any[]).length > 0,
      };
    },
  });

  const lostMut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("bids")
        .update({ status: "lost" })
        .eq("bid_id", bidId!);
      if (error) throw error;
    },
    onSuccess: () => refetch(),
  });

  if (isLoading) return <div className="py-20 flex justify-center"><Spinner /></div>;
  if (!bid) return <div className="p-6">BID not found.</div>;

  const isOpen = bid.status === "open";
  const canAct = hasRole(user, "manager");
  const days   = daysFromNow(bid.due_date);
  const site   = bid.sites;

  const tile = (label: string, value: React.ReactNode, sub?: React.ReactNode, tone?: "ok" | "warn" | "danger", onClick?: () => void) => (
    <button type="button" onClick={onClick} disabled={!onClick} className={`text-left rounded-lg border border-line bg-ink-card p-3 min-w-0 ${onClick ? "hover:border-line-light" : "cursor-default"}`}>
      <div className="text-[10px] uppercase tracking-wider2 text-text-muted">{label}</div>
      <div className={`font-mono text-[18px] leading-tight mt-0.5 truncate ${tone === "danger" ? "text-danger" : tone === "warn" ? "text-warn" : tone === "ok" ? "text-ok" : ""}`}>{value}</div>
      {sub && <div className="text-[11px] text-text-dim truncate">{sub}</div>}
    </button>
  );

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6">
      <button onClick={() => navigate("/bids")} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft size={14}/> Bids
      </button>

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[13px] text-accent">{bid.bid_number}</span>
            <Pill label={bid.status} tone={bid.status === "won" ? "won" : bid.status === "lost" ? "lost" : "bid"} />
            {isOpen && days != null && days <= 3 && (
              <Pill label={days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? "due today" : `due in ${days}d`} tone="critical" small />
            )}
          </div>
          <h1 className="text-[22px] font-semibold tracking-tight mt-1">{bid.bid_title}</h1>
          <div className="text-text-muted text-[13px] mt-1 flex items-center gap-1.5 flex-wrap">
            <span>{bid.clients?.legal_name}</span>
            {bid.carriers?.carrier_code && <span>· {bid.carriers.carrier_code}</span>}
            {bid.client_ref && <span>· ref <span className="font-mono">{bid.client_ref}</span></span>}
            {site?.site_name && <span>· {site.site_name}</span>}
            {site?.rfnsa_id && <a href={`https://www.rfnsa.com.au/${encodeURIComponent(String(site.rfnsa_id).trim())}`} target="_blank" rel="noopener noreferrer" className="font-mono text-accent hover:underline inline-flex items-center gap-1">· RFNSA {site.rfnsa_id} <ExternalLink size={10}/></a>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isOpen && canAct && (
            <>
              <Button icon={TrendingUp} onClick={() => setShowPromote(true)}>Won — promote</Button>
              <Button variant="danger" icon={TrendingDown} onClick={() => { if (confirm("Mark this BID as lost? Any open quotes against it will also be rejected.")) lostMut.mutate(); }}>Lost</Button>
            </>
          )}
          {bid.status === "won" && bid.promoted_to_project_id && (
            <Link to={`/projects/${bid.promoted_to_project_id}`}>
              <Button variant="secondary" icon={ExternalLink}>Open project</Button>
            </Link>
          )}
        </div>
      </div>

      {/* the strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
        {tile("Client's budget", bid.estimated_value ? fmtMoney(bid.estimated_value) : "—", bid.estimated_value ? "their figure, not ours" : "none given")}
        {tile("RFQ due", bid.due_date ? fmtDate(bid.due_date) : "—", days == null ? undefined : days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? "today" : `in ${days}d`, isOpen && days != null && days < 0 ? "danger" : isOpen && days != null && days <= 3 ? "warn" : undefined)}
        {tile("Quotes", strip ? strip.quotes : "…", strip ? (strip.accepted ? `${strip.accepted} accepted · ${fmtMoney(strip.quoted)}` : strip.sent ? `${strip.sent} sent · ${fmtMoney(strip.quoted)}` : strip.drafts ? `${strip.drafts} draft${strip.drafts === 1 ? "" : "s"}` : "none yet") : undefined, strip?.accepted ? "ok" : strip?.quotes === 0 && isOpen ? "warn" : undefined, () => setTab("overview"))}
        {tile("Documents", strip ? strip.docs : "…", strip ? (strip.designDocs ? `${strip.designDocs} design / FC` : strip.docs ? "no FC among them" : "nothing filed") : undefined, strip && isOpen && !strip.designDocs ? "warn" : undefined, () => setTab("documents"))}
        {tile("Design", strip?.design ? "filled" : "—", strip?.design ? `${strip.design.source === "ai" ? "by the worker" : "typed"} · ${fmtDate(strip.design.updated_at)}` : "template empty", strip?.design ? "ok" : undefined, () => setTab("design"))}
        {tile("Program", strip?.program ? "planned" : "—", strip?.program ? "on the build program" : "not planned yet", strip?.program ? "ok" : undefined, () => setTab("plan"))}
      </div>

      <div className="flex items-center gap-1 mb-5 border-b border-line overflow-x-auto">
        {([
          ["overview",  "Overview",        Briefcase],
          ["design",    "Design",          DraftingCompass],
          ["plan",      "Plan & program",  CalendarRange],
          ["documents", "Documents",       FolderOpen],
          ...(canAct ? [["hse", "HSE", Shield] as const] : []),
        ] as const).map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key as BidTab)}
            className={`px-4 py-2.5 text-[13px] font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 whitespace-nowrap ${tab === key ? "border-accent text-text" : "border-transparent text-text-muted hover:text-text"}`}>
            <Icon size={13} />{label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SummaryCard bid={bid} days={days} canEdit={canAct}/>
            <LinkedQuotesPanel bid={bid}/>
          </div>
          <BidSitesCard bidId={bid.bid_id} canEdit={canAct}/>
          {bid.scope_summary && (
            <Card>
              <div className="p-5">
                <SectionLabel>Scope</SectionLabel>
                <div className="text-[13px] text-text whitespace-pre-wrap mt-2">{bid.scope_summary}</div>
              </div>
            </Card>
          )}
          <BidNotepadCard bidId={bid.bid_id} bidNumber={bid.bid_number} canEdit={canAct}/>
        </div>
      )}

      {tab === "design" && (
        <div className="space-y-4">
          {canAct && strip && !strip.design && strip.designDocs > 0 && (
            <div className="rounded-lg border border-accent/30 bg-accent/8 px-4 py-3 text-[12.5px] flex items-center gap-2 flex-wrap">
              <Sparkles size={14} className="text-accent"/> {strip.designDocs} design document{strip.designDocs === 1 ? "" : "s"} on this BID and the template is empty — an SoR quote's <span className="font-medium">Read the FC</span> fills it from the pack.
            </div>
          )}
          <DesignPanel parent={{ kind: "bid", id: bid.bid_id, code: bid.bid_number, name: bid.bid_title, siteId: (bid as any).site_id ?? null }} canEdit={canAct} />
        </div>
      )}

      {tab === "plan" && (
        <div className="space-y-4">
          <BidSchedulePlanCard bidId={bid.bid_id} canEdit={canAct}/>
          {canAct && (
            <ProgramPanel
              parent={{ kind: "bid", id: bid.bid_id, code: bid.bid_number, name: bid.bid_title }}
              preparedBy={[user?.first_name, user?.last_name].filter(Boolean).join(" ") || "Coretel Group"}
              siteLine={(bid as any).sites?.site_name ?? null}
            />
          )}
          <BidDeliverablesCard bidId={bid.bid_id} canEdit={canAct}/>
        </div>
      )}

      {tab === "documents" && (
        <div className="space-y-4">
          <JobFolderCard kind="bid" id={bid.bid_id} folderUrl={(bid as any).sharepoint_folder_url ?? null} promotedProjectId={(bid as any).promoted_to_project_id ?? null} canManage={canAct} onChanged={() => refetch()}/>
          <DocumentsPanel bidId={bid.bid_id} canEdit={canAct}/>
        </div>
      )}

      {tab === "hse" && canAct && (
        <div className="space-y-4">
          <BidMopsCard bidId={bid.bid_id}/>
          <SiteMapsCard bidId={bid.bid_id} canEdit={canAct}/>
        </div>
      )}

      {showPromote && (
        <PromoteDialog
          bid={bid}
          onClose={() => setShowPromote(false)}
          onPromoted={(projectId) => navigate(`/projects/${projectId}`)}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Summary card — value, due, site with distance, ref
// -----------------------------------------------------------------------------
function SummaryCard({ bid, days, canEdit }: { bid: any; days: number | null; canEdit?: boolean }) {
  const site = bid.sites;
  // 3 Nov 2026 — James: "For the site change on the BID I just want this box
  // to be editable and site changed from the site list we have, select
  // another and save, that's it." Writes bids.site_id and keeps the primary
  // bid_sites row in step (the same pair BidSitesCard's Make primary writes).
  const qc = useQueryClient();
  const [changing, setChanging] = useState(false);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState<any | null>(null);
  const { data: options = [] } = useQuery({
    queryKey: ["bid_change_site_options", q], enabled: changing,
    queryFn: async () => {
      let qq: any = supabase.from("sites").select("site_id, site_code, site_name, suburb, state").order("site_code").limit(30);
      if (q.trim()) qq = qq.or(`site_code.ilike.%${q.trim()}%,site_name.ilike.%${q.trim()}%,suburb.ilike.%${q.trim()}%`);
      const { data, error } = await qq; if (error) throw error; return (data ?? []) as any[];
    },
  });
  const changeSite = useMutation({
    mutationFn: async () => {
      if (!pick) return;
      const { data: prim } = await supabase.from("bid_sites").select("bid_site_id").eq("bid_id", bid.bid_id).eq("is_primary", true).maybeSingle();
      if (prim?.bid_site_id) {
        const { error } = await supabase.from("bid_sites").update({ site_id: pick.site_id }).eq("bid_site_id", prim.bid_site_id); if (error) throw error;
      } else {
        const { error } = await supabase.from("bid_sites").insert({ bid_id: bid.bid_id, site_id: pick.site_id, is_primary: true, sort_order: 0 }); if (error) throw error;
      }
      const { error: e2 } = await supabase.from("bids").update({ site_id: pick.site_id }).eq("bid_id", bid.bid_id); if (e2) throw e2;
    },
    onSuccess: () => { setChanging(false); setPick(null); setQ(""); qc.invalidateQueries({ queryKey: ["bid", bid.bid_id] }); qc.invalidateQueries({ queryKey: ["bid_sites", bid.bid_id] }); },
    onError: (e: any) => window.alert(`Couldn't change the site: ${e?.message ?? String(e)}`),
  });
  const lat  = site?.latitude  ?? site?.lat  ?? null;
  const lng  = site?.longitude ?? site?.lng  ?? null;
  const km   = distanceFromBneGpoKm(lat, lng);   // straight line — the road figure comes from <DistanceFromBne/>

  // Try every plausible address shape — sites schema has varied over time.
  const address =
    site?.address_line1 ??
    site?.street_address ??
    site?.address ??
    null;
  const suburb = site?.suburb ?? site?.locality ?? null;
  const state  = site?.state  ?? site?.region   ?? null;

  const dueText =
    days == null ? fmtDate(bid.due_date)
    : days <  0  ? `${fmtDate(bid.due_date)} · ${Math.abs(days)} days overdue`
    : days === 0 ? `${fmtDate(bid.due_date)} · today`
    :              `${fmtDate(bid.due_date)} · in ${days} day${days === 1 ? "" : "s"}`;

  return (
    <Card>
      <div className="p-5 space-y-4">
        <SectionLabel>Summary</SectionLabel>
        <KV label="Estimated value" value={fmtMoney(bid.estimated_value)} mono />
        <KV
          label="RFQ due"
          value={
            <span className={days != null && days <= 3 && bid.status === "open" ? "text-danger" : ""}>
              <Clock size={11} className="inline mr-1.5 align-[-1px] text-text-muted"/>
              {dueText}
            </span>
          }
        />
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium">Site</div>
            {canEdit && !changing && <button type="button" className="text-[11px] text-accent hover:underline" onClick={() => setChanging(true)}>{site ? "Change site" : "Pick a site"}</button>}
          </div>
          {changing && (
            <div className="mb-3 rounded-md border border-warn/50 bg-warn/[0.06] p-2.5 text-[12px]">
              <input autoFocus value={q} onChange={e => { setQ(e.target.value); setPick(null); }} placeholder="Search by site code, name or suburb…" className="w-full bg-ink border border-line rounded px-2 py-1.5 text-[12.5px] text-text mb-2"/>
              <div className="max-h-[180px] overflow-y-auto divide-y divide-line border border-line rounded-md bg-ink">
                {options.filter((x: any) => x.site_id !== site?.site_id).map((x: any) => (
                  <button key={x.site_id} type="button" onClick={() => setPick(x)} className={`w-full text-left px-2 py-1.5 hover:bg-ink-raised ${pick?.site_id === x.site_id ? "bg-accent/10" : ""}`}>
                    <span className="font-mono text-[11.5px] text-accent">{x.site_code}</span> <span className="text-text">{x.site_name}</span> <span className="text-text-muted">{[x.suburb, x.state].filter(Boolean).join(" ")}</span>
                  </button>
                ))}
                {options.length === 0 && <div className="px-2 py-1.5 text-text-muted">No site matches.</div>}
              </div>
              <div className="mt-2 flex gap-2 justify-end">
                <Button size="sm" variant="secondary" onClick={() => { setChanging(false); setPick(null); }} disabled={changeSite.isPending}>Cancel</Button>
                <Button size="sm" disabled={!pick || changeSite.isPending} onClick={() => changeSite.mutate()}>{changeSite.isPending ? "Saving…" : "Save"}</Button>
              </div>
            </div>
          )}
          {site ? (
            <div className="text-[13px] text-text space-y-0.5">
              <div className="font-mono text-[12px] text-accent">{site.site_code ?? "—"}</div>
              <div>{site.site_name ?? ""}</div>
              {/* 18 Sep 2026 — James: "RFNSA Number HAS to be on this page, its used
                  more than anything else to check site details online." Same
                  sites.rfnsa_id the project page shows; links straight to the
                  RFNSA site page. */}
              <div className="text-[12px]">
                <span className="text-text-muted">RFNSA </span>
                {site.rfnsa_id
                  ? <a href={`https://www.rfnsa.com.au/${encodeURIComponent(String(site.rfnsa_id).trim())}`} target="_blank" rel="noopener noreferrer"
                       className="font-mono text-accent hover:underline inline-flex items-center gap-1">
                      {site.rfnsa_id} <ExternalLink size={10}/>
                    </a>
                  : <span className="text-text-dim">not on the site record — <Link to={`/sites/${site.site_id}`} className="text-accent hover:underline">add it</Link></span>}
              </div>
              {site.asset_owner?.owner_code && (
                <div className="text-text-muted text-[12px]">
                  Owner: <span className="font-mono">{site.asset_owner.owner_code}</span> · {site.asset_owner.display_name}
                </div>
              )}
              {address && <div className="text-text-muted text-[12px]">{address}</div>}
              {(suburb || state) && (
                <div className="text-text-muted text-[12px]">
                  {[suburb, state].filter(Boolean).join(", ")}
                </div>
              )}
              {km != null && (
                <div className="text-text-muted text-[11px] font-mono mt-1 inline-flex items-start gap-1.5">
                  <MapPin size={10} className="mt-0.5"/> <DistanceFromBne site={{ ...site, latitude: lat, longitude: lng }} canSave/>
                </div>
              )}
            </div>
          ) : (
            <div className="text-text-dim text-[13px]">—</div>
          )}
        </div>
        <KV label="Client reference" value={bid.client_ref || <span className="text-text-dim">—</span>} />
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// MOPs on this BID (mig 380) — the register rows whose bid_id is this bid.
// "New MOP" opens the builder with the BID preselected (?bid=).
// -----------------------------------------------------------------------------
function BidMopsCard({ bidId }: { bidId: string }) {
  const { data: mops = [], isLoading } = useQuery({
    queryKey: ["bid_mops", bidId],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("mops")
        .select("mop_id, mop_number, title, status, version, approved_at, created_at")
        .eq("bid_id", bidId)
        .neq("status", "superseded")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  return (
    <Card>
      <div className="p-5">
        <div className="flex items-center justify-between gap-2 mb-3">
          <SectionLabel>MOPs {mops.length > 0 && <span className="ml-1 font-mono text-text-muted">({mops.length})</span>}</SectionLabel>
          <Link to={`/hse/mops?bid=${bidId}`}>
            <Button variant="ghost" icon={Plus} size="sm">New MOP</Button>
          </Link>
        </div>
        {isLoading ? (
          <div className="py-3 flex justify-center"><Spinner /></div>
        ) : mops.length === 0 ? (
          <div className="py-3 text-[12px] text-text-muted">
            None yet. A MOP written here is managers-only and moves onto the project by itself when the BID is won.
          </div>
        ) : (
          <div className="space-y-1.5">
            {mops.map((m: any) => (
              <Link key={m.mop_id} to={`/hse/mops/${m.mop_id}`}>
                <div className="px-3 py-2 rounded border border-line hover:border-accent/30 hover:bg-ink-hover transition-colors flex items-center gap-3">
                  <span className="font-mono text-[11px] text-accent w-[104px] shrink-0">{m.mop_number}</span>
                  <div className="flex-1 min-w-0 text-[13px] truncate">{m.title}</div>
                  {m.status === "approved"
                    ? <Pill label={`approved v${m.version}`} tone="won" small/>
                    : <Pill label="DRAFT" tone="pending" small/>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Linked quotes panel — show all quotes pointing at this BID
// -----------------------------------------------------------------------------
function LinkedQuotesPanel({ bid }: { bid: any }) {
  const { data: quotes = [], isLoading } = useQuery({
    queryKey: ["bid_quotes", bid.bid_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quotes")
        .select(`quote_id, quote_number, status, subtotal_excl_gst, total_incl_gst, quote_date, valid_until, accepted_at, sent_at`)
        .eq("bid_id", bid.bid_id)
        .order("quote_date", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });

  return (
    <Card>
      <div className="p-5">
        <div className="flex items-center justify-between gap-2 mb-3">
          <SectionLabel>Quotes {quotes.length > 0 && <span className="ml-1 font-mono text-text-muted">({quotes.length})</span>}</SectionLabel>
          {bid.status !== "lost" && (
            <Link to={`/quotes/new?bid_id=${bid.bid_id}`}>
              <Button variant="ghost" icon={Plus} size="sm">New quote</Button>
            </Link>
          )}
        </div>

        {isLoading ? (
          <div className="py-3 flex justify-center"><Spinner /></div>
        ) : quotes.length === 0 ? (
          <div className="py-4 text-center">
            <FileText size={18} className="text-text-dim mx-auto mb-2"/>
            <div className="text-[12px] text-text-muted">No quotes on this BID yet.</div>
            {bid.status !== "lost" && (
              <div className="text-[11px] text-text-dim mt-1">Click <span className="text-text-muted">New quote</span> to draft one.</div>
            )}
          </div>
        ) : (
          <div className="space-y-1.5">
            {quotes.map((q) => (
              <Link key={q.quote_id} to={`/quotes/${q.quote_id}`}>
                <div className="px-3 py-2 rounded border border-line hover:border-accent/30 hover:bg-ink-hover transition-colors flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-accent">{q.quote_number}</span>
                      <Pill
                        label={q.status}
                        tone={q.status === "accepted" ? "won" : q.status === "rejected" ? "lost" : q.status === "sent" ? "mobilising" : "pending"}
                        small
                      />
                    </div>
                    <div className="text-[11px] font-mono text-text-muted mt-0.5">
                      {q.quote_date ? fmtDate(q.quote_date) : "—"}
                      {q.valid_until && ` · valid to ${fmtDate(q.valid_until)}`}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-[12px] text-text">{fmtMoney(q.subtotal_excl_gst)}</div>
                    <div className="font-mono text-[10px] text-text-muted">ex GST</div>
                  </div>
                  <ChevronRight size={12} className="text-text-dim"/>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Documents panel — upload, list, download, delete
// -----------------------------------------------------------------------------
const DOC_TYPES = [
  { value: "design",        label: "Design",        tone: "bid"       as const },
  { value: "drawing",       label: "Drawing",       tone: "bid"       as const },
  { value: "scope",         label: "Scope",         tone: "pending"   as const },
  { value: "rfq",           label: "RFQ",           tone: "pending"   as const },
  { value: "specification", label: "Spec",          tone: "pending"   as const },
  { value: "sow",           label: "SoW",           tone: "pending"   as const },
  { value: "email",         label: "Email",         tone: "pending"   as const },
  { value: "photo",         label: "Photo",         tone: "pending"   as const },
  { value: "other",         label: "Other",         tone: "pending"   as const },
];

function DocumentsPanel({ bidId, canEdit }: { bidId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState<string>("design");
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["bid_documents", bidId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bid_documents")
        .select("id, bid_id, project_id, file_name, file_path, file_size_bytes, mime_type, doc_type, title, uploaded_at")
        .eq("bid_id", bidId)
        .order("uploaded_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploadError(null);
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        const safeName = file.name.replace(/[^A-Za-z0-9._-]/g, "_");
        const path = `${bidId}/${ts}-${safeName}`;

        const { error: upErr } = await supabase.storage
          .from("bid-documents")
          .upload(path, file, { contentType: file.type || "application/octet-stream", upsert: false });
        if (upErr) throw upErr;

        const { error: insErr } = await supabase.from("bid_documents").insert({
          bid_id:          bidId,
          file_name:       file.name,
          file_path:       path,
          file_size_bytes: file.size,
          mime_type:       file.type || null,
          doc_type:        uploadType,
        });
        if (insErr) throw insErr;
      }
      qc.invalidateQueries({ queryKey: ["bid_documents", bidId] });
    } catch (e: any) {
      setUploadError(e?.message ?? String(e));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const deleteMut = useMutation({
    mutationFn: async (doc: any) => {
      const { error: sErr } = await supabase.storage
        .from("bid-documents")
        .remove([doc.file_path]);
      if (sErr) throw sErr;
      const { error: dErr } = await supabase.from("bid_documents")
        .delete().eq("id", doc.id);
      if (dErr) throw dErr;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bid_documents", bidId] }),
  });

  async function handleDownload(doc: any) {
    const { data, error } = await supabase.storage
      .from("bid-documents")
      .createSignedUrl(doc.file_path, 60 * 10); // 10 min TTL
    if (error) { alert(error.message); return; }
    if (data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  }

  function fmtBytes(n: number | null | undefined): string {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n/1024).toFixed(0)} KB`;
    return `${(n/1024/1024).toFixed(1)} MB`;
  }

  return (
    <Card>
      <div className="p-5">
        <div className="flex items-center justify-between gap-2 mb-3">
          <SectionLabel>
            <Paperclip size={11} className="inline mr-1.5 align-[-1px]"/>
            Documents {docs.length > 0 && <span className="ml-1 font-mono text-text-muted">({docs.length})</span>}
          </SectionLabel>
        </div>

        {canEdit && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[11px] text-text-muted">Upload as:</span>
              <Select
                value={uploadType}
                onChange={(e: any) => setUploadType(e.target.value)}
                className="!text-[11px] !py-1 !w-auto"
              >
                {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
            </div>
            <label
              className={`block rounded border border-dashed border-line hover:border-accent/40 bg-ink/40 px-4 py-6 text-center cursor-pointer transition-colors ${uploading ? "opacity-60 cursor-wait" : ""}`}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleFiles(e.dataTransfer.files); }}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                disabled={uploading}
                onChange={(e) => handleFiles(e.target.files)}
              />
              <Upload size={16} className="text-text-muted mx-auto mb-1.5"/>
              <div className="text-[12px] text-text-muted">
                {uploading ? "Uploading…" : "Drop files here or click to browse"}
              </div>
              <div className="text-[10px] text-text-dim mt-1">
                PDF, DWG, images, Word — up to 50 MB each
              </div>
            </label>
            {uploadError && (
              <div className="mt-2 bg-danger/10 border border-danger/20 rounded px-3 py-2 text-[12px] text-danger">
                <AlertTriangle size={11} className="inline mr-1"/>
                {uploadError}
              </div>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="py-3 flex justify-center"><Spinner/></div>
        ) : docs.length === 0 ? (
          <div className="py-4 text-center">
            <FileText size={18} className="text-text-dim mx-auto mb-2"/>
            <div className="text-[12px] text-text-muted">No documents uploaded yet.</div>
          </div>
        ) : (
          <div className="space-y-1.5">
            {docs.map((d) => {
              const typeMeta = DOC_TYPES.find(t => t.value === d.doc_type) ?? DOC_TYPES[DOC_TYPES.length - 1];
              return (
                <div key={d.id} className="px-3 py-2 rounded border border-line hover:bg-ink-hover transition-colors flex items-center gap-3">
                  <Pill label={typeMeta.label} tone={typeMeta.tone} small/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-text truncate">{d.file_name}</div>
                    <div className="text-[10px] font-mono text-text-muted">
                      {fmtBytes(d.file_size_bytes)} · uploaded {fmtDate(d.uploaded_at)}
                      {d.project_id && <span className="ml-2 text-ok">· on project</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownload(d)}
                    className="p-1.5 rounded hover:bg-ink text-text-muted hover:text-text"
                    title="Download"
                  >
                    <Download size={14}/>
                  </button>
                  {canEdit && (
                    <button
                      onClick={() => { if (confirm(`Delete ${d.file_name}?`)) deleteMut.mutate(d); }}
                      className="p-1.5 rounded hover:bg-ink text-text-muted hover:text-danger"
                      title="Delete"
                    >
                      <Trash2 size={14}/>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Promote to project dialog (existing RPC — used when there's no quote path)
// -----------------------------------------------------------------------------
function PromoteDialog({ bid, onClose, onPromoted }: { bid: any; onClose: () => void; onPromoted: (id: string) => void }) {
  const [contract_value, setContractValue] = useState(String(bid.estimated_value ?? ""));
  const [scheduled_start_date, setStartDate] = useState("");
  const [scheduled_end_date, setEndDate] = useState("");
  const [client_po_reference, setPoRef] = useState("");

  const promoteMut = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc("promote_bid_to_project", {
        p_bid_id: bid.bid_id,
        p_contract_value: contract_value ? parseFloat(contract_value) : null,
        p_scheduled_start_date: scheduled_start_date || null,
        p_scheduled_end_date:   scheduled_end_date || null,
        p_client_po_reference:  client_po_reference || null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (projectId) => onPromoted(projectId),
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center md:justify-center" onClick={onClose}>
      <div className="bg-ink-card border border-line w-full md:max-w-md md:rounded-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-line">
          <h3 className="text-lg font-semibold">Promote to project</h3>
          <div className="text-[12px] text-text-muted mt-1">
            Creates a full project record with a project code, flips this BID to "won", carries over documents, and links them.
          </div>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Contract value (ex-GST)">
            <Input type="number" mono value={contract_value} onChange={e => setContractValue(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date"><Input type="date" mono value={scheduled_start_date} onChange={e => setStartDate(e.target.value)} /></Field>
            <Field label="End date"><Input type="date" mono value={scheduled_end_date} onChange={e => setEndDate(e.target.value)} /></Field>
          </div>
          <Field label="Client PO reference">
            <Input mono value={client_po_reference} onChange={e => setPoRef(e.target.value)} placeholder="PO-xxxxx" />
          </Field>
          {promoteMut.isError && (
            <div className="bg-danger/10 border border-danger/20 rounded px-3 py-2 text-[12px] text-danger">
              <AlertTriangle size={11} className="inline mr-1" />
              {String((promoteMut.error as any)?.message ?? promoteMut.error)}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-line bg-ink-raised flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button icon={CheckCircle2} onClick={() => promoteMut.mutate()} disabled={promoteMut.isPending}>
            {promoteMut.isPending ? "Promoting…" : "Promote"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Small helpers
// -----------------------------------------------------------------------------
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">{children}</div>
  );
}

function KV({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3">
      <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium pt-0.5">{label}</div>
      <div className={`text-[13px] text-text ${mono ? "font-mono" : ""}`}>{value ?? <span className="text-text-dim">—</span>}</div>
    </div>
  );
}
